import { type FriendDto, type FriendsDto, type MeDto } from '@spin/shared';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { game } from '../api/game';
import { http } from '../api/http';
import { Dialog } from '../components/Dialog';
import { Avatar, Spinner } from '../components/common';
import { reportError, useT } from '../i18n';
import { useSession } from '../store/session';
import { useUi } from '../store/ui';
import { tg } from '../tg/bridge';

const SoundIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
    <path d="M3 9h4l5-4v14l-5-4H3Z" fill="#7ac943" />
    <path
      d="M15 9a4 4 0 0 1 0 6M17.5 6.5a7.5 7.5 0 0 1 0 11"
      stroke="#7ac943"
      strokeWidth="2"
      fill="none"
      strokeLinecap="round"
    />
  </svg>
);
const MusicIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
    <path d="M9 18V5l11-2v13" stroke="#e044c8" strokeWidth="2.4" fill="none" />
    <circle cx="6.5" cy="18" r="3" fill="#ea50c9" />
    <circle cx="17.5" cy="16" r="3" fill="#ea50c9" />
  </svg>
);
const InviteIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
    <circle cx="9" cy="8" r="4" fill="#f2c9a5" />
    <path d="M2 21c1-5 4-7 7-7s6 2 7 7Z" fill="#7d8aa8" />
    <circle cx="18" cy="8" r="5" fill="#7ac943" />
    <path d="M18 5.5v5M15.5 8h5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);
const FriendsIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
    <circle cx="12" cy="5" r="3.5" fill="#29a3e8" />
    <path d="M7 10h10l-1.5 6H14v6h-4v-6H8.5Z" fill="#29a3e8" />
  </svg>
);
const ContactIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
    <path d="M2 4h14v9H7l-4 3v-3H2Z" fill="#9cc3e6" />
    <path d="M9 9h13v9h-1v3l-4-3H9Z" fill="#5c8fd6" />
  </svg>
);

function Row({
  icon,
  label,
  children,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  children?: ReactNode;
  onClick?: () => void;
}) {
  const body = (
    <>
      <span className="settings-icon">{icon}</span>
      <span className="settings-label">{label}</span>
      {children}
    </>
  );
  return onClick ? (
    <button className="settings-row" onClick={onClick}>
      {body}
    </button>
  ) : (
    <div className="settings-row">{body}</div>
  );
}

/** Settings (the gear): sound volume, music, invite friends, my friends, contact us and the player's id. */
export function SettingsDialog() {
  const t = useT();
  const me = useSession((s) => s.me)!;
  const support = useSession((s) => s.config?.support.telegram ?? null);
  const { openDialog } = useUi.getState();
  const [volume, setVolume] = useState(me.soundVolume);

  const save = async (patch: Partial<Pick<MeDto, 'soundVolume' | 'musicEnabled'>>) => {
    try {
      useSession.getState().setMe(await http.patch<MeDto>('/me', patch));
    } catch (err) {
      reportError(err);
    }
  };

  return (
    <Dialog title={t('settings.title')} centered className="settings-dialog" testId="settings">
      <Row icon={<SoundIcon />} label={t('settings.sounds')}>
        <input
          className="slider"
          type="range"
          min={0}
          max={100}
          value={volume}
          style={{ '--fill': `${volume}%` } as React.CSSProperties}
          onChange={(e) => setVolume(Number(e.target.value))}
          onPointerUp={() => volume !== me.soundVolume && void save({ soundVolume: volume })}
          onKeyUp={() => volume !== me.soundVolume && void save({ soundVolume: volume })}
          aria-label={t('settings.sounds')}
          data-testid="sound-volume"
        />
      </Row>
      <Row icon={<MusicIcon />} label={t('settings.music')}>
        <button
          className={me.musicEnabled ? 'switch on' : 'switch'}
          role="switch"
          aria-checked={me.musicEnabled}
          aria-label={t('settings.music')}
          onClick={() => void save({ musicEnabled: !me.musicEnabled })}
          data-testid="music-switch"
        >
          <i />
        </button>
      </Row>
      <Row
        icon={<InviteIcon />}
        label={t('settings.invite')}
        onClick={() => openDialog('referral', undefined, { stack: true })}
      />
      <Row
        icon={<FriendsIcon />}
        label={t('settings.friends')}
        onClick={() => openDialog('friends', undefined, { stack: true })}
      />
      {support && (
        <Row
          icon={<ContactIcon />}
          label={t('settings.contact')}
          onClick={() => tg.openTelegramUrl(support)}
        />
      )}
      <div className="settings-id" data-testid="game-id">
        ID {me.gameId}
      </div>
    </Dialog>
  );
}

/** "Friends list": who can see my friends, friend requests to answer, and my friends (join their table). */
export function FriendsDialog() {
  const t = useT();
  const me = useSession((s) => s.me)!;
  const [data, setData] = useState<FriendsDto | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    http.get<FriendsDto>('/friends').then(setData).catch(reportError);
  }, []);
  useEffect(load, [load]);

  const act = async (run: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await run();
      tg.haptic('light');
      load();
    } catch (err) {
      reportError(err);
    } finally {
      setBusy(false);
    }
  };

  const setPrivacy = (friendsVisibility: 'everyone' | 'me') =>
    act(async () => useSession.getState().setMe(await http.patch<MeDto>('/me', { friendsVisibility })));

  const row = (f: FriendDto, actions: ReactNode) => (
    <div key={f.userId} className="friend-row" data-testid={`friend-${f.userId}`}>
      <span className="friend-avatar">
        <Avatar name={f.displayName} photoUrl={f.photoUrl} size={36} />
        {f.online && <i className="online-dot" aria-label={t('friends.online')} />}
      </span>
      <span className="ellipsis friend-name">{f.displayName}</span>
      {actions}
    </div>
  );

  return (
    <Dialog title={t('friends.title')} centered className="friends-dialog" testId="friends">
      <p className="friends-q">{t('friends.who_sees')}</p>
      <div className="radios">
        {(['everyone', 'me'] as const).map((v) => (
          <label key={v}>
            <input
              type="radio"
              name="friends-visibility"
              checked={me.friendsVisibility === v}
              disabled={busy}
              onChange={() => void setPrivacy(v)}
            />
            {t(`friends.visibility_${v}`)}
          </label>
        ))}
      </div>
      {!data ? (
        <Spinner />
      ) : (
        <>
          {data.incoming.map((f) =>
            row(
              f,
              <span className="row">
                <button
                  className="btn btn-green"
                  disabled={busy}
                  onClick={() => void act(() => http.post(`/friends/${f.userId}`))}
                  data-testid="accept-friend"
                >
                  {t('friends.accept')}
                </button>
                <button
                  className="btn"
                  disabled={busy}
                  onClick={() => void act(() => http.delete(`/friends/${f.userId}`))}
                >
                  ✕
                </button>
              </span>,
            ),
          )}
          {data.friends.length === 0 && data.incoming.length === 0 ? (
            <p className="friends-empty">{t('friends.empty')}</p>
          ) : (
            data.friends.map((f) =>
              row(
                f,
                f.roomId ? (
                  <button
                    className="btn btn-green"
                    disabled={busy}
                    onClick={() =>
                      void act(async () => {
                        await game.join(f.roomId!);
                        useUi.getState().closeDialog();
                      })
                    }
                  >
                    {t('friends.join')}
                  </button>
                ) : (
                  <button
                    className="btn"
                    disabled={busy}
                    onClick={() => void act(() => http.delete(`/friends/${f.userId}`))}
                    aria-label={t('friends.remove')}
                    title={t('friends.remove')}
                  >
                    ✕
                  </button>
                ),
              ),
            )
          )}
          {data.outgoing.length > 0 && (
            <p className="muted friends-sent">{t('friends.sent', { count: data.outgoing.length })}</p>
          )}
        </>
      )}
    </Dialog>
  );
}
